import { Texture } from '@pixi/core';
import { Sprite } from '@pixi/sprite';
import gsap from 'gsap';

import { App, Shared } from '../App';
import { AnalyticsEvent } from '../plugins/AnalyticsPlugin';
import { NineSliceButton } from './buttons/NineSliceButton';
import LocalisedText from './LocalisedText';
import { Panel } from './Panel';
import { RankBadge } from './RankBadge';
import { RankPointsLabel } from './RankPointsLabel';
import { RankTitleLabel } from './RankTitleLabel';
import { UserIdentity } from './UserIdentity';
import { Widget } from './Widget';

export class RankPanel extends Widget
{
    public app: App;
    private panel: Panel;
    private badge: RankBadge;
    private identity: UserIdentity;
    private identityButton: NineSliceButton;
    private rankTitle: RankTitleLabel;
    private points: RankPointsLabel;
    private info: Sprite;
    private tapArea: Sprite;

    constructor(app: App)
    {
        super();
        this.app = app;

        this.panel = new Panel({
            w: this.width,
            h: this.height,
        });
        this.view.addChild(this.panel.view);

        this.badge = new RankBadge();
        this.panel.view.addChild(this.badge);

        this.identity = new UserIdentity();
        this.panel.view.addChild(this.identity);
        this.identity.y = (-this.height / 2) + 40;
        this.identity.scale.set(0.75);

        this.rankTitle = new RankTitleLabel();
        this.panel.view.addChild(this.rankTitle);
        this.rankTitle.y = this.identity.y + 32;
        this.rankTitle.scale.set(0.75);

        this.points = new RankPointsLabel();
        this.points.x = -70;
        this.points.y = -70;
        this.panel.view.addChild(this.points);
        this.points.scale.set(0.7);

        this.info = new Sprite(Shared.assets.getTexture('icon-info'));
        this.info.anchor.set(0.5);
        this.panel.view.addChild(this.info);
        this.info.x = 80;
        this.info.y = -75;

        this.tapArea = new Sprite(Texture.WHITE);
        this.tapArea.width = this.width;
        this.tapArea.height = this.height;
        this.tapArea.anchor.set(0.5);
        this.tapArea.alpha = 0;
        this.panel.view.addChild(this.tapArea);
        this.tapArea.on('pointertap', () => this.onPointerTap());
        this.tapArea.on('pointerover', () => this.onPointerOver());
        this.tapArea.on('pointerout', () => this.onPointerOut());

        this.identityButton = this.createIdentityButton();
        this.panel.view.addChild(this.identityButton.view);
        this.identityButton.view.y = this.height * 0.35;
    }

    public get width(): number
    {
        return 290;
    }

    public get height(): number
    {
        return 400;
    }

    protected async onOpen(): Promise<void>
    {
        this.refresh();
        gsap.killTweensOf(this.panel.view);
        this.panel.view.x = -500;
        await gsap.to(this.panel.view, { x: 0, duration: 0.5, ease: 'expo.out', delay: 0.1 });
        this.tapArea.interactive = true;
        this.tapArea.buttonMode = true;
    }

    protected async onClose(): Promise<void>
    {
        this.tapArea.interactive = false;
        this.tapArea.buttonMode = false;
        gsap.killTweensOf(this.panel.view);
        await gsap.to(this.panel.view, { x: -500, duration: 0.3, ease: 'sine.in' });
    }

    public refresh(): void
    {
        const status = this.app.rank.getRankStatus();

        this.identity.refresh();
        this.rankTitle.refresh(status.title, status.tier);
        this.points.refresh(status.points);
        this.badge.refresh(status);
    }

    private createIdentityButton(): NineSliceButton
    {
        const buttonContent = new Sprite();
        const width = 220;
        const height = 70;

        const buttonLabel = new LocalisedText('settingsScreen_name', {
            fontFamily: 'lubalin',
            fill: 0xFFFFFF,
            stroke: 0x333333,
            strokeThickness: 6,
            lineJoin: 'round',
            align: 'center',
            fontSize: 26,
            maxWidth: width * 0.9,
        });

        buttonLabel.anchor.set(0.5);
        buttonContent.addChild(buttonLabel);

        const button = new NineSliceButton({
            icon: buttonContent,
            bgAsset: 'button-ninesliced-yellow',
            w: width,
            h: height,
        });

        button.onPress.connect(() =>
        {
            this.app.analytics.trackEvent(AnalyticsEvent.HOMEPAGE_CHANGEID_CLICK, {game_item: `changeid|${Shared.settings.playerName}` });
            Shared.controller.toChangeName(false, true);
        });

        return button;
    }

    private onPointerOver(): void
    {
        gsap.to(this.info.scale, { x: 1.25, y: 1.25, duration: 0.3, ease: 'back.out' });
    }

    private onPointerOut(): void
    {
        gsap.to(this.info.scale, { x: 1, y: 1, duration: 0.3, ease: 'back.out' });
    }

    private onPointerTap(): void
    {
        this.app.popup.presentRankStatsPopup();
    }
}
